import { useEffect, useState } from 'react';
import { Form, FormikProvider, useFormikContext } from 'formik';
import { toFormikValidate, toFormikValidationSchema } from 'zod-formik-adapter';
import { Text } from '@antimatterhq/ui/components/typography';
import { Label } from '@antimatterhq/ui/components/label';
import Spacer from '@antimatterhq/ui/components/spacer';
import { ButtonGroup } from '@antimatterhq/ui/components/buttonGroup';
import { PresetHeading } from '../../_partials/PresetHeading';
import { useFormikWithAsyncValidation } from '@/hooks/form/useFormikWithAsyncValidation';
import { getBronzeInitialValues, setBronzeFormValues } from '@/lib/datasource';
import { FormError, FormInput, FormTextarea } from '@/components/form/form';
import { DataFormatDropdown } from '@/components/dropdown';
import {
  BronzeDataSourceSchema,
  BronzeDataSourceSchemaValues,
} from '@/zod/schemas/dataSource/BronzeDataSource';
import { DataSourceNavigationButtons } from '@/components/button/dataSourceNavigation/DataSourceNavigationButtons';
import {
  DatasourceSubmitType,
  useConfigDataSource,
} from '@/contexts/ConfigDataSourceContext';
import { FormDatabricksPathAndTableValidatedInput } from '@/components/form/input/FormDatabricksPathAndTableValidatedInput';
import {
  dsSkipBronzeTable,
  dsUseAutoloader,
  schemaFileTooltip,
  schemaHintsTooltip,
} from '@/strings/StringsTooltips';
import FormDatabricksPathValidatedTypeahead from '@/components/typeahead/externalLocations/FormDatabricksPathValidatedTypeahead';
import { TwoColumnLayout } from '@/components/container';
import { AsyncValidationProvider } from '@/contexts/AsyncValidationContext';

export type BronzeFormProps = {
  submitAction: DatasourceSubmitType;
  setSubmitAction: (action: DatasourceSubmitType) => void;
  submitDisabled?: boolean;
};

const BronzeForm = ({
  submitAction,
  setSubmitAction,
  submitDisabled = false,
}: BronzeFormProps) => {
  const { handlePreviousStep, isSaving, setFormik } = useConfigDataSource();

  const { values, handleChange, handleBlur, dirty, handleSubmit } =
    useFormikContext<BronzeDataSourceSchemaValues>();

  useEffect(() => {
    setFormik({ handleSubmit, dirty });
  }, [handleSubmit, dirty, setFormik]);

  return (
    <Form data-testid="bronze-form">
      <Label
        htmlFor="name"
        required
        data-testid="bronze-name-label"
        id="bronze-name-label"
      >
        Bronze table name
      </Label>
      <FormDatabricksPathAndTableValidatedInput
        id="name"
        name="name"
        autoComplete="off"
        disabled={isSaving}
        constraint="mustExistReadWriteOrBeCreatable"
        validationType="table"
        tableSchema="bronzeSchema"
        data-testid="bronze-name-input"
        aria-labelledby="bronze-name-label"
        required
      />
      <FormError name="name" data-testid="bronze-name-error" />

      <Spacer size="lg" />

      <Text
        size="xl"
        className="mt-4 block"
        data-testid="bronze-autoloader-title"
        id="bronze-autoloader-title"
        bold
      >
        Auto loader parameters
      </Text>

      <Spacer size="lg" />

      <Label data-testid="bronze-loading-label">Bronze loading</Label>
      <Spacer size="sm" />

      <>
        <ButtonGroup
          options={[
            {
              label: 'Use auto loader',
              value: 'configure',
              info: { content: dsUseAutoloader },
            },
            {
              label: 'Skip loading bronze table',
              value: 'skip',
              info: { content: dsSkipBronzeTable },
            },
          ]}
          value={values.skip ? 'skip' : 'configure'}
          onChange={(value) =>
            handleChange({
              target: { name: 'skip', value: value === 'skip' },
            })
          }
          disabled={isSaving}
          data-testid="bronze-autoloader-buttongroup"
        />
      </>

      {!values.skip && (
        <>
          <Spacer size="lg" />

          <Label
            htmlFor="location"
            required
            data-testid="bronze-location-label"
          >
            Location of datasource
          </Label>
          <FormDatabricksPathValidatedTypeahead
            name="location"
            constraint="mustExistReadWriteOrBeCreatable"
            disabled={isSaving}
            data-testid="bronze-location-typeahead"
            placeholder="Enter an s3://, dbfs:/ or /Volumes/ path"
          />
          <FormError name="location" data-testid="bronze-location-error" />

          <Spacer size="lg" />

          <Label
            htmlFor="format"
            id="bronze-format-label"
            required
            data-testid="bronze-format-label"
          >
            Data format
          </Label>
          <div className="max-w-form">
            <DataFormatDropdown
              aria-labelledby="bronze-format-label"
              name="format"
              value={values.format}
              onValueChange={(value) =>
                handleChange({ target: { name: 'format', value } })
              }
              disabled={isSaving}
              data-testid="bronze-format-dropdown"
            />
          </div>
          <FormError name="format" data-testid="bronze-format-error" />

          <Spacer size="lg" />

          <Label data-testid="bronze-schema-title">Schema</Label>
          <div data-testid="bronze-schema-buttongroup-container">
            <ButtonGroup
              options={[
                {
                  label: 'Schema hints',
                  value: 'hints',
                  info: { content: schemaHintsTooltip },
                },
                {
                  label: 'Schema hints file',
                  value: 'hintsFile',
                  info: { content: schemaHintsTooltip },
                },
                {
                  label: 'Schema file',
                  value: 'file',
                  info: { content: schemaFileTooltip },
                },
              ]}
              value={values.schema}
              onChange={(value) =>
                handleChange({
                  target: { name: 'schema', value },
                })
              }
              disabled={isSaving || values.skip}
              data-testid="bronze-schema-buttongroup"
            />
          </div>

          <Spacer size="lg" />

          {values.schema === 'file' && (
            <>
              <Label
                htmlFor="schemaFile"
                id="bronze-schema-file-label"
                data-testid="bronze-schema-file-label"
              >
                Schema file
              </Label>
              <FormInput
                aria-labelledby="bronze-schema-file-label"
                id="schemaFile"
                name="schemaFile"
                value={values.schemaFile}
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="off"
                disabled={isSaving}
                data-testid="bronze-schema-file-input"
              />
              <FormError
                name="schemaFile"
                data-testid="bronze-schema-file-error"
              />
            </>
          )}

          {values.schema === 'hints' && (
            <>
              <Label
                htmlFor="schemaHints"
                id="bronze-schema-hints-label"
                data-testid="bronze-schema-hints-label"
              >
                Schema hints
              </Label>
              <FormTextarea
                aria-labelledby="bronze-schema-hints-label"
                id="schemaHints"
                name="schemaHints"
                value={values.schemaHints}
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="off"
                disabled={isSaving}
                draggable
                className="min-h-[150px]"
                data-testid="bronze-schema-hints-textarea"
              />
              <FormError
                name="schemaHints"
                data-testid="bronze-schema-hints-error"
              />
            </>
          )}

          {values.schema === 'hintsFile' && (
            <>
              <Label
                htmlFor="schemaHintsFile"
                id="bronze-schema-hints-file-label"
                data-testid="bronze-schema-hints-file-label"
              >
                Schema hints file
              </Label>
              <FormInput
                aria-labelledby="bronze-schema-hints-file-label"
                id="schemaHintsFile"
                name="schemaHintsFile"
                value={values.schemaHintsFile}
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="off"
                disabled={isSaving}
                data-testid="bronze-schema-hints-file-input"
              />
              <FormError
                name="schemaHintsFile"
                data-testid="bronze-schema-hints-file-error"
              />
            </>
          )}
        </>
      )}

      <Spacer size="lg" />

      <DataSourceNavigationButtons
        disabled={isSaving || submitDisabled}
        goToPreviousStep={handlePreviousStep}
        handlePreviousStep={() => setSubmitAction('previous')}
        handleNextStep={() => setSubmitAction('next')}
        handleReview={() => setSubmitAction('review')}
        loadingNext={submitAction === 'next' && isSaving}
        loadingReview={submitAction === 'review' && isSaving}
        data-testid="bronze-navigation-buttons"
      />
    </Form>
  );
};

function DataSourceBronzeContent() {
  const { dataSource, handleNextStep, handleReview, handlePreviousStep, save } =
    useConfigDataSource();

  const [submitAction, setSubmitAction] = useState<DatasourceSubmitType>('');
  const formik = useFormikWithAsyncValidation({
    initialValues: getBronzeInitialValues(dataSource),
    validate: toFormikValidate(BronzeDataSourceSchema),
    validationSchema: toFormikValidationSchema(BronzeDataSourceSchema),
    onSubmit: async (newValues) => {
      const newDataSource = setBronzeFormValues(dataSource, newValues);
      const result = await save(newDataSource);

      if (!result) {
        return;
      }

      // go to next step
      if (submitAction === 'next') {
        handleNextStep();
      } else if (submitAction === 'review') {
        handleReview();
      } else if (submitAction === 'previous') {
        handlePreviousStep();
      }
    },
  });

  return (
    <TwoColumnLayout.Root data-testid="bronze-layout-root">
      <TwoColumnLayout.LeftColumn>
        <PresetHeading />
        <FormikProvider value={formik}>
          <BronzeForm
            submitAction={submitAction}
            setSubmitAction={setSubmitAction}
            submitDisabled={formik.submitDisabled}
          />
        </FormikProvider>
      </TwoColumnLayout.LeftColumn>
      <TwoColumnLayout.RightColumn></TwoColumnLayout.RightColumn>
    </TwoColumnLayout.Root>
  );
}

export default function DataSourceBronze() {
  return (
    <AsyncValidationProvider>
      <DataSourceBronzeContent />
    </AsyncValidationProvider>
  );
}
